import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AppShell } from "./components/AppShell.jsx";
import { DataMasterPage } from "./pages/DataMasterPage.jsx";
import { PendingOrdersPage } from "./pages/PendingOrdersPage.jsx";
import { FleetOrdersPage } from "./pages/FleetOrdersPage.jsx";
import { DoPreparationsPage } from "./pages/DoPreparationsPage.jsx";

const PAGE_PATHS = {
  "pending-orders": "/",
  "fleet-orders": "/fleet-orders",
  "do-preparations": "/pembuatan-do",
  "master-data": "/data-master",
};

function getPageFromPath(pathname) {
  if (pathname.startsWith("/fleet-orders")) return "fleet-orders";
  if (pathname.startsWith("/pembuatan-do")) return "do-preparations";
  if (pathname.startsWith("/data-master")) return "master-data";
  return "pending-orders";
}

function getCurrentBrowserUrl() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function dispatchBeforeNavigate(detail) {
  return window.dispatchEvent(
    new CustomEvent("route-planning:before-navigate", {
      cancelable: true,
      detail,
    }),
  );
}

function resetDocumentScroll() {
  const scrollingElement = document.scrollingElement || document.documentElement;

  if (scrollingElement) {
    scrollingElement.scrollTop = 0;
    scrollingElement.scrollLeft = 0;
  }

  document.documentElement.scrollTop = 0;
  document.documentElement.scrollLeft = 0;
  document.body.scrollTop = 0;
  document.body.scrollLeft = 0;

  const appContent = document.querySelector(".app-content");
  appContent?.scrollTo?.({ top: 0, left: 0, behavior: "auto" });
  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
}

export default function RootApp() {
  const [activePage, setActivePage] = useState(() =>
    getPageFromPath(window.location.pathname),
  );
  const currentUrlRef = useRef(getCurrentBrowserUrl());
  const currentHistoryStateRef = useRef(window.history.state);

  useEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";

    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  useLayoutEffect(() => {
    resetDocumentScroll();

    const frameId = window.requestAnimationFrame(resetDocumentScroll);
    return () => window.cancelAnimationFrame(frameId);
  }, [activePage]);

  useEffect(() => {
    function publishLocationChanged(source) {
      window.dispatchEvent(
        new CustomEvent("route-planning:location-changed", {
          detail: {
            source,
            state: window.history.state,
            url: getCurrentBrowserUrl(),
          },
        }),
      );
    }

    function handleLocationCommitted(event) {
      currentUrlRef.current = event.detail?.url || getCurrentBrowserUrl();
      currentHistoryStateRef.current = event.detail?.state ?? window.history.state;
    }

    function handlePopState() {
      const targetUrl = getCurrentBrowserUrl();
      const allowed = dispatchBeforeNavigate({
        source: "history",
        targetUrl,
      });

      if (!allowed) {
        window.history.pushState(
          currentHistoryStateRef.current,
          "",
          currentUrlRef.current,
        );
        return;
      }

      currentUrlRef.current = targetUrl;
      currentHistoryStateRef.current = window.history.state;
      setActivePage(getPageFromPath(window.location.pathname));
      publishLocationChanged("history");
    }

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("route-planning:location-committed", handleLocationCommitted);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener(
        "route-planning:location-committed",
        handleLocationCommitted,
      );
    };
  }, []);

  function navigate(page) {
    const targetPath = PAGE_PATHS[page] || PAGE_PATHS["pending-orders"];
    const allowed = dispatchBeforeNavigate({
      source: "primary-navigation",
      targetPage: page,
      targetUrl: targetPath,
    });

    if (!allowed) return;

    if (getCurrentBrowserUrl() !== targetPath) {
      const nextState = {
        routePlanningPage: page,
      };

      window.history.pushState(nextState, "", targetPath);
      currentHistoryStateRef.current = nextState;
      currentUrlRef.current = targetPath;
    }

    setActivePage(page);
    window.dispatchEvent(
      new CustomEvent("route-planning:location-changed", {
        detail: {
          source: "primary-navigation",
          state: window.history.state,
          url: getCurrentBrowserUrl(),
        },
      }),
    );
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }

  return (
    <AppShell activePage={activePage} onNavigate={navigate}>
      {activePage === "master-data" ? (
        <DataMasterPage />
      ) : activePage === "fleet-orders" ? (
        <FleetOrdersPage />
      ) : activePage === "do-preparations" ? (
        <DoPreparationsPage />
      ) : (
        <PendingOrdersPage />
      )}
    </AppShell>
  );
}
